// direct-relay reads: primal normally serves all feed content from its caching
// service, so long-form articles that live only on a user's own relays never
// appear. this fetches kind 30023 straight from the user's relays and shapes
// them into the same PrimalArticle objects the reads feed already renders.

import { SimplePool } from './nTools';
import { Kind } from '../constants';
import { convertToReadsMega } from '../stores/megaFeed';
import { emptyMegaFeedPage } from '../megaFeeds';
import { accountStore, getRelayUrls } from '../stores/accountStore';
import { PrimalArticle } from '../types/primal';
import { logError } from './logger';

type FetchOpts = {
  // published_at/created_at upper bound (exclusive) for pagination
  until?: number,
  limit?: number,
  // restrict to a single author; omit for all authors on the relays
  authors?: string[],
};

const RELAY_TIMEOUT = 6000;

// the cache normally supplies per-article word counts (used for "min read");
// relays don't, so derive it from the markdown content instead.
const countWords = (content: string): number => {
  const words = (content || '').trim().match(/\S+/g);
  return words ? words.length : 0;
};

// fetch long-form articles + their authors' kind 0 metadata directly from the
// user's relays and convert to PrimalArticle[]. engagement stats aren't served
// by plain relays, so they render as zero (convertToReadsMega defaults them).
export const fetchDirectRelayReads = async (opts: FetchOpts = {}): Promise<PrimalArticle[]> => {
  const relays = getRelayUrls();

  // when proxying through primal, direct relay connections are suspended
  // elsewhere; skip so we don't open sockets the user opted out of.
  if (accountStore.proxyThroughPrimal || relays.length === 0) return [];

  const pool = new SimplePool();

  try {
    const filter: {
      kinds: number[],
      limit: number,
      until?: number,
      authors?: string[],
    } = {
      kinds: [Kind.LongForm],
      limit: opts.limit ?? 20,
    };

    if (opts.until && opts.until > 0) filter.until = opts.until;
    if (opts.authors && opts.authors.length > 0) filter.authors = opts.authors;

    const articleEvents = await pool.querySync(relays, filter, { maxWait: RELAY_TIMEOUT });

    if (articleEvents.length === 0) return [];

    // dedup by address (kind:pubkey:d-tag), keeping the newest revision — a
    // relay may hold several revisions of the same article under one d-tag.
    const byAddress = new Map<string, typeof articleEvents[number]>();
    for (const ev of articleEvents) {
      const d = (ev.tags.find((t) => t[0] === 'd') || [])[1] || '';
      const address = `${ev.kind}:${ev.pubkey}:${d}`;
      const existing = byAddress.get(address);
      if (!existing || ev.created_at > existing.created_at) {
        byAddress.set(address, ev);
      }
    }
    const reads = [...byAddress.values()];

    // backfill author profiles (kind 0) so articles render with a real byline
    const authorPubkeys = [...new Set(reads.map((e) => e.pubkey))];
    const profileEvents = await pool.querySync(
      relays,
      { kinds: [Kind.Metadata], authors: authorPubkeys },
      { maxWait: RELAY_TIMEOUT },
    );

    const page = emptyMegaFeedPage();
    // @ts-ignore relay events are structurally the note-content shape the page expects
    page.reads = reads;
    for (const p of profileEvents) {
      // @ts-ignore
      page.users[p.pubkey] = p;
    }
    for (const r of reads) {
      page.wordCount[r.id] = countWords(r.content);
    }

    return convertToReadsMega(page);
  } catch (reason) {
    logError('failed to fetch reads directly from relays: ', reason);
    return [];
  } finally {
    try { pool.close(getRelayUrls()); } catch { /* pool already closed */ }
  }
};

// fetch a single long-form article by its address (pubkey + d-tag identifier)
// straight from the user's relays, for the article-detail page when the
// caching service doesn't have it. returns undefined if not found.
export const fetchDirectRelayArticle = async (
  pubkey: string,
  identifier: string,
): Promise<PrimalArticle | undefined> => {
  const relays = getRelayUrls();

  if (accountStore.proxyThroughPrimal || relays.length === 0) return undefined;

  const pool = new SimplePool();

  try {
    const events = await pool.querySync(
      relays,
      { kinds: [Kind.LongForm], authors: [pubkey], '#d': [identifier], limit: 5 },
      { maxWait: RELAY_TIMEOUT },
    );

    if (events.length === 0) return undefined;

    // keep the newest revision of this address
    const article = events.reduce((a, b) => (b.created_at > a.created_at ? b : a));

    const profiles = await pool.querySync(
      relays,
      { kinds: [Kind.Metadata], authors: [pubkey] },
      { maxWait: RELAY_TIMEOUT },
    );

    const page = emptyMegaFeedPage();
    // @ts-ignore relay event matches the note-content shape the page expects
    page.reads = [article];
    page.wordCount[article.id] = countWords(article.content);
    const profile = profiles.reduce<typeof profiles[number] | undefined>(
      (acc, p) => (!acc || p.created_at > acc.created_at ? p : acc),
      undefined,
    );
    // @ts-ignore
    if (profile) page.users[pubkey] = profile;

    return convertToReadsMega(page)[0];
  } catch (reason) {
    logError('failed to fetch article directly from relays: ', reason);
    return undefined;
  } finally {
    try { pool.close(getRelayUrls()); } catch { /* pool already closed */ }
  }
};
